import { DOCUMENT, ErrorHandler, inject, Injectable } from '@angular/core';
import { defer, from, map, Observable, take } from 'rxjs';
import {
  AssetLoadError,
  DynamicLoaderAssets,
  DynamicLoaderAssetWithToken,
  DynamicLoaderInlineAssets,
  DynamicLoaderResponse,
  DynamicLoaderStatuses,
} from './domain';

@Injectable({
  providedIn: 'root',
})
export class DynamicAssetLoaderService {
  private readonly document = inject(DOCUMENT);
  private readonly errorHandler = inject(ErrorHandler);
  private readonly loaded: { [key: string]: boolean } = {};
  private readonly pendingLoads: { [key: string]: Promise<DynamicLoaderResponse> } = {};

  /**
   * Load an asset when you need to do something after it has loaded.
   *
   * e.g: I need to load this library before I call a function from it.
   *
   * This should only be used when you need to do something after the script has loaded.
   *
   * @param assets (DynamicLoaderAssets | DynamicLoaderAssetWithToken)[]
   *
   * @returns Observable<boolean>
   */
  public load(...assets: (DynamicLoaderAssets | DynamicLoaderAssetWithToken)[]): Observable<boolean> {
    const promises: Promise<DynamicLoaderResponse>[] = assets.map((asset) => this.loadAsset(this.getAssetUrl(asset)));

    return from(defer(() => Promise.all(promises))).pipe(
      take(1),
      map((assetStatuses) => {
        const failure = assetStatuses.find((asset) => asset.status === DynamicLoaderStatuses.Failed);
        if (failure) {
          const error = new AssetLoadError(failure.asset);
          // PostHogErrorHandler records this as an event, which sends nothing without a PostHog client
          // (dev, server), so the warning keeps the failure visible there.
          console.warn(error.message);
          this.errorHandler.handleError(error);
        }

        return true;
      }),
    );
  }

  /**
   * Load an asset when you don't need to do anything after it has loaded.
   *
   * e.g: This library needs to be loaded, but I am not doing anything with it
   *
   * @param asset DynamicLoaderAssets | DynamicLoaderAssetWithToken
   */
  public appendAsset(asset: DynamicLoaderAssets | DynamicLoaderAssetWithToken): void {
    const url = this.getAssetUrl(asset);

    if (!this.loaded[url]) {
      const element = this.getElementByExtension(url);
      this.appendToDocumentHead(element);
      this.loaded[url] = true;
    }
  }

  /**
   * Add an inline script to the DOM
   *
   * @param asset DynamicLoaderInlineAssets
   * @param code string
   */
  public appendInlineAsset(asset: DynamicLoaderInlineAssets, code: string): void {
    if (!this.loaded[asset]) {
      const element = this.baseScriptElement();
      element.text = code;
      this.appendToDocumentHead(element);

      this.loaded[asset] = true;
    }
  }

  /**
   * Add an inline asset with a custom tag and custom innerHtml to the DOM
   *
   * @param asset DynamicLoaderInlineAssets
   * @param elementName string
   * @param elementInnerHtml string
   */
  public appendInlineAssetWithCustomContent(asset: DynamicLoaderInlineAssets, elementName: string, elementInnerHtml: string): void {
    if (!this.loaded[asset]) {
      const element: HTMLElement = this.document.createElement(elementName);
      element.innerHTML = elementInnerHtml;
      this.appendToDocumentHead(element);

      this.loaded[asset] = true;
    }
  }

  private assetPromise(asset: string, element: HTMLElement): Promise<DynamicLoaderResponse> {
    return new Promise((resolve) => {
      element.onload = () => {
        this.loaded[asset] = true;
        resolve({ asset, status: DynamicLoaderStatuses.Loaded });
      };

      element.onerror = () => {
        this.loaded[asset] = false;
        resolve({ asset, status: DynamicLoaderStatuses.Failed });
      };

      this.appendToDocumentHead(element);
    });
  }

  private appendToDocumentHead(element: HTMLElement) {
    this.document.getElementsByTagName('head')[0].appendChild(element);
  }

  private baseScriptElement() {
    const scriptTag = this.document.createElement('script');
    scriptTag.type = 'text/javascript';
    return scriptTag;
  }

  private getAssetUrl(asset: DynamicLoaderAssets | DynamicLoaderAssetWithToken): string {
    return typeof asset === 'string' ? asset : asset.asset.replace('{TOKEN}', asset.token);
  }

  private getElementByExtension(asset: string): HTMLElement {
    if (asset.endsWith('.css')) {
      return this.styleElement(asset);
    }

    return this.scriptElement(asset);
  }

  // A caller who arrives while a load is already in flight awaits the same
  // promise instead of appending a second element to the DOM.
  private loadAsset(asset: string): Promise<DynamicLoaderResponse> {
    if (this.loaded[asset]) {
      return Promise.resolve({ asset, status: DynamicLoaderStatuses.Loaded });
    }

    const pending = this.pendingLoads[asset];
    if (pending) {
      return pending;
    }

    const element = this.getElementByExtension(asset);
    const load = this.assetPromise(asset, element).finally(() => {
      delete this.pendingLoads[asset];
    });
    this.pendingLoads[asset] = load;

    return load;
  }

  private scriptElement(asset: string): HTMLElement {
    const scriptTag = this.baseScriptElement();
    scriptTag.src = asset;
    scriptTag.async = true;
    scriptTag.defer = true;

    return scriptTag;
  }

  private styleElement(asset: string): HTMLElement {
    const linkTag = this.document.createElement('link');
    linkTag.rel = 'stylesheet';
    linkTag.href = asset;
    return linkTag;
  }
}
